import Button from '@rio-cloud/rio-uikit/Button';
import NotBookedState from '@rio-cloud/rio-uikit/components/states/NotBookedState';
import Divider from '@rio-cloud/rio-uikit/Divider';
import Fade from '@rio-cloud/rio-uikit/Fade';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TourStopDetails } from '#components/tourstopdetails/TourStopDetails.tsx';
import { extractLanguage } from '#configuration/lang/lang.ts';
import { useLocale } from '#configuration/lang/langSlice.ts';
import {
    gaPush,
    getTrackingAttributes,
    TRACKING_ACTIONS,
    TRACKING_CATEGORIES,
} from '#configuration/setup/googleAnalytics.ts';
import { getUpsellingDemoData, upsellingDemoDriver } from '#features/livemonitor/upselling/data/upsellingDemoTour.ts';
import { WidgetTourDetails } from '#features/livemonitor/WidgetTourDetails.tsx';
import { useTrackScreenSeen } from '#hooks/useTrackScreenSeen.ts';

export const WidgetUpsellingView = () => {
    const ref = useRef(null);
    const onScreen = useOnScreen(ref);
    const [selectedStopId, setSelectedStopId] = useState<string>();
    const locale = useLocale();
    const intl = useIntl();
    const { upsellingDemoTour } = getUpsellingDemoData(intl);
    const productInformationUrl = `https://rio.cloud/${extractLanguage(locale)}/marketplace/produkt/order-communication`;

    useTrackScreenSeen(onScreen, {
        category: TRACKING_CATEGORIES.LIVEMONITOR,
        action: TRACKING_ACTIONS.ON_SCREEN,
        label: 'Show upselling widget',
    });

    useEffect(() => {
        if (!selectedStopId) {
            return;
        }

        if (!upsellingDemoTour.stops.some(stop => stop.id === selectedStopId)) {
            setSelectedStopId(undefined);
        }
    }, [selectedStopId]);

    const selectedStop = upsellingDemoTour.stops.find(stop => stop.id === selectedStopId);
    const hasSelectedStop = !!selectedStopId && !!selectedStop;
    const animationStyle = hasSelectedStop ? 'page' : 'pageBack';

    return (
        <div
            data-testid='upselling-view'
            className={classNames({ 'widget-dev-styles': import.meta.env.DEV })}
            ref={ref}
            {...getTrackingAttributes({
                trigger: 'visibility',
                category: TRACKING_CATEGORIES.LIVEMONITOR,
                action: TRACKING_ACTIONS.RENDER,
                label: 'Render upselling widget',
                value: '',
            })}
        >
            <NotBookedState
                headline={<FormattedMessage id='intl-msg:order-communication-web.livemonitor.premium.headline' />}
                message={
                    <div className='display-flex flex-column align-items-center gap-10 text-wrap-balance'>
                        <FormattedMessage id='intl-msg:order-communication-web.livemonitor.premium.description' />
                        <Button
                            as='a'
                            href={productInformationUrl}
                            onClick={() =>
                                gaPush({
                                    category: TRACKING_CATEGORIES.LIVEMONITOR,
                                    action: TRACKING_ACTIONS.PRESS_BUTTON,
                                    label: 'Clicked learn more link in upselling advertisement',
                                })
                            }
                            target='_top'
                            rel='noreferrer'
                            bsStyle='primary'
                        >
                            <FormattedMessage
                                id='intl-msg:order-communication-web.livemonitor.premium.learnMore'
                                defaultMessage='Learn more'
                            />
                        </Button>
                    </div>
                }
                outerClassName='border-none padding-0'
            />
            <Divider spacing={20} dividerColor='lighter' titleAlignment='center'>
                <span className='text-size-12 text-color-dark text-medium'>
                    <FormattedMessage id='intl-msg:order-communication-web.livemonitor.premium.demo.title' />
                </span>
            </Divider>
            <Fade
                initial={false}
                exitBeforeEnter
                animationStyle={animationStyle}
                duration={0.25}
                motionProps={{ transition: { duration: 0.25, type: 'spring' } }}
            >
                {!hasSelectedStop && (
                    <div className='padding-x-15'>
                        <WidgetTourDetails
                            key='tour-details'
                            title={upsellingDemoTour.searchName}
                            tour={upsellingDemoTour}
                            driver={upsellingDemoDriver}
                            setSelectedStopId={setSelectedStopId}
                        />
                    </div>
                )}
                {hasSelectedStop && selectedStop && (
                    <TourStopDetails
                        title={selectedStop.name}
                        key='tour-stop-details'
                        onBack={() => {
                            setSelectedStopId(undefined);
                        }}
                        stop={selectedStop}
                        transportOrders={upsellingDemoTour.transportOrders.filter(order =>
                            selectedStop.transportOrderIds.includes(order.id)
                        )}
                        serviceOrders={upsellingDemoTour.serviceOrders.filter(order =>
                            selectedStop.serviceOrderIds.includes(order.id)
                        )}
                        type='widget'
                    />
                )}
            </Fade>
        </div>
    );
};
